import React from "react";
import { Trans, useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { Tooltip } from "src/ui/shared/Tooltip";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { RoleLabel } from "@dashboard/shared/components/RoleLabel";
import { getAssignableRoleOptions, getRoleLabel, rolesOptions } from "@dashboard/shared/roles";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { DialogContentText } from "@mui/material";
import AuthService from "@services/authService";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { showSuccessToast } from "@shared/toasts/Toasts";

function validateRole(role: unknown) {
    if (role !== "Owner" && role !== "Manager" && role !== "User") {
        throw new Error(i18n.t("pages:workspacePage.toast.invalidRole"));
    }
    return role;
}

type Props = {
    member: WorkspaceMember;
};

export const ChangeWorkspaceRoleField = (props: Props) => {
    const [pendingRole, setPendingRole] = React.useState<WorkspaceMember["role"] | null>(null);
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();
    const { t } = useTranslation(["common", "pages", "modals", "sidepanels"]);

    const isExpired = useCurrentSubscriptionExpired();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: workspace } = useCurrentWorkspace();

    const { workspaceAbility } = PermissionService();
    const ability = workspaceAbility(workspace);
    const canManageMembers = ability.can("manage", "Member");
    const canAssignOwner = ability.can("update", memberSubject("Owner"));
    const mayEditThisMember = ability.can("update", memberSubject(props.member.role));
    const options = getAssignableRoleOptions(canAssignOwner);
    const isOwnRole = props.member.email === AuthService.getCurrentUser()?.email;
    const ownerCount = workspace?.members.filter((member) => member.role === "Owner").length ?? 0;
    const isLastOwner = props.member.role === "Owner" && ownerCount === 1;

    const onRoleChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string" || selectedOption.value === props.member.role) {
            return;
        }
        setPendingRole(validateRole(selectedOption.value));
    };

    const onConfirmRoleChange = async () => {
        if (!workspaceId || !pendingRole) {
            return;
        }

        await updateWorkspaceMembers({
            id: workspaceId,
            changedMembers: { [props.member.email]: pendingRole },
        }).unwrap();
        showSuccessToast(t("pages:workspacePage.toast.membersUpdateSuccess"));
    };

    const getTooltip = () => {
        // Expiry blocks every role change, so it outranks the owner-specific message.
        if (isExpired) {
            return t("pages:workspacePage.tooltip.subscriptionExpired");
        }
        if (!mayEditThisMember) {
            return t("sidepanels:groupPanel.tooltip.cannotEditOwnerRole");
        }
        if (isLastOwner) {
            return t("pages:workspacePage.tooltip.lastOwner");
        }
        return null;
    };

    const tooltip = getTooltip();

    // A member who cannot manage members can never edit roles, so show the role as plain text.
    if (!canManageMembers) {
        return <RoleLabel role={props.member.role} />;
    }

    return (
        <React.Fragment>
            <Tooltip title={tooltip} disableChildrenWrapper>
                <SelectField
                    value={rolesOptions.find((option) => option.value === props.member.role)}
                    options={options}
                    onChange={onRoleChange}
                    disabled={!!tooltip}
                    fullWidth
                    disableSorting
                />
            </Tooltip>

            <ConfirmDialog
                title={t("modals:changeMemberRole.title")}
                primaryActionText={t("common:button.change")}
                open={!!pendingRole}
                onConfirm={onConfirmRoleChange}
                onClose={() => setPendingRole(null)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="changeMemberRole.descriptionWorkspace"
                        values={{
                            email: props.member.email,
                            name: workspace?.name,
                            oldRole: getRoleLabel(props.member.role),
                            newRole: getRoleLabel(pendingRole),
                        }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
                {isOwnRole ? <CustomAlert severity="warning">{t("modals:changeMemberRole.ownRoleWarning")}</CustomAlert> : null}
            </ConfirmDialog>
        </React.Fragment>
    );
};
